import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
} from '@1trading/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { justificationSchema } from '@/components/dialogs/confirm-action';
import { JustificationField } from '@/components/dialogs/justification-field';
import { FieldError } from '@/components/ui/field-error';
import { parseExclusionError } from '@/features/fee-runs/fee-run-exclusions';
import { useSetFeeRunExclusion } from '@/hooks/queries/fees/use-fee-runs';
import type { FeeRunExclusion, FeeRunExclusionReason } from '@/services/api/fee-runs';
import { ACCOUNT_HOLDER_ID_LENGTH } from '@/utils/validation';

const exclusionFormSchema = z.object({
  pid: z.string().trim().length(ACCOUNT_HOLDER_ID_LENGTH, `A PID is exactly ${ACCOUNT_HOLDER_ID_LENGTH} characters`),
  reason: z.string().min(1, 'Choose a reason'),
  justification: justificationSchema,
});

type ExclusionFormSchema = z.infer<typeof exclusionFormSchema>;

interface FeeRunExclusionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reasons: FeeRunExclusionReason[];
  /** Set to change an existing exclusion's reason; the PID is then fixed */
  editing: FeeRunExclusion | null;
}

export const FeeRunExclusionDialog = ({ open, onOpenChange, reasons, editing }: FeeRunExclusionDialogProps) => {
  const setExclusion = useSetFeeRunExclusion();
  const defaults = (): ExclusionFormSchema => ({ pid: editing?.pid ?? '', reason: editing?.reason ?? '', justification: '' });
  const form = useForm<ExclusionFormSchema>({ resolver: zodResolver(exclusionFormSchema), mode: 'onChange', defaultValues: defaults() });
  const { isValid, errors } = form.formState;
  const isSubmitting = setExclusion.isPending;

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset only when the dialog opens
  useEffect(() => {
    if (!open) return;
    form.reset(defaults());
    setExclusion.reset();
  }, [open]);

  const justification = form.watch('justification');
  const submitError = setExclusion.isError ? parseExclusionError(setExclusion.error) : null;

  const handleSubmit = form.handleSubmit((values) => {
    setExclusion.mutate(
      { pid: values.pid, reason: values.reason, comment: values.justification.trim() },
      {
        onSuccess: () => {
          toast.success(editing ? 'Exclusion updated' : 'Account holder excluded from fee runs');
          onOpenChange(false);
        },
        onError: (error) => {
          const parsed = parseExclusionError(error);
          if (parsed.field) form.setError(parsed.field, { message: parsed.message });
        },
      },
    );
  });

  return (
    <Dialog onOpenChange={(next) => (next || !isSubmitting) && onOpenChange(next)} open={open}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{editing ? 'Change exclusion' : 'Exclude from fee runs'}</DialogTitle>
          <DialogDescription>
            The account holder is not charged custody or platform fees by any run calculated from now on. A run already calculated must be calculated again before it can be
            approved.
          </DialogDescription>
        </DialogHeader>
        <form className="flex flex-col gap-4" noValidate onSubmit={handleSubmit}>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="fee-exclusion-pid">Account Holder ID (PID)</Label>
            <Input aria-invalid={!!errors.pid} className="font-mono" disabled={!!editing} id="fee-exclusion-pid" {...form.register('pid')} />
            <FieldError message={errors.pid?.message} />
          </div>
          <Controller
            control={form.control}
            name="reason"
            render={({ field }) => (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="fee-exclusion-reason">Reason</Label>
                <Select onValueChange={field.onChange} value={field.value || undefined}>
                  <SelectTrigger id="fee-exclusion-reason">
                    <SelectValue placeholder="Choose a reason" />
                  </SelectTrigger>
                  <SelectContent>
                    {reasons.map((reason) => (
                      <SelectItem key={reason.value} value={reason.value}>
                        {reason.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          />

          <JustificationField id="fee-exclusion-justification" justification={justification} prompt="Why is this account holder excluded?" {...form.register('justification')} />

          {submitError && !submitError.field ? (
            <Alert variant="destructive">
              <AlertDescription>{submitError.message}</AlertDescription>
            </Alert>
          ) : null}

          <DialogFooter>
            <Button disabled={isSubmitting} onClick={() => onOpenChange(false)} type="button" variant="outline">
              Cancel
            </Button>
            <Button className="gap-2" disabled={!isValid || isSubmitting} type="submit" variant="primary">
              {isSubmitting ? <Spinner className="size-4" /> : null}
              {editing ? 'Save' : 'Exclude'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
FeeRunExclusionDialog.displayName = 'FeeRunExclusionDialog';
